import { useCallback, useEffect, useState } from 'react';
import {
  getMoleculeAssertions,
  getProperties,
  getPropertySummaries,
  isStatementAssertion,
  isStatementProperty,
  type Assertion,
  type PropertyDefinition,
  type PropertySummary,
  type StatementAssertion
} from '@peruselab/api-client/assertions';
import type { PublishedStatement } from '@peruselab/api-client/molecules';

export type MoleculeProperties = {
  /** The quantity properties (melting point, logP…). Statements are apart, below (#947). */
  readonly catalog: readonly PropertyDefinition[];
  readonly assertions: readonly Assertion[];
  readonly summaries: readonly PropertySummary[];
  /** #947: statements -- descriptions, uses, toxicity summaries -- as Text assertions. */
  readonly statements: readonly StatementAssertion[];
};

export interface MoleculePropertiesState {
  /** Null until the first answer, and after a failure. */
  readonly loaded: MoleculeProperties | null;
  readonly failed: boolean;
  readonly reload: () => Promise<void>;
}

/** A value is current until it's superseded or withdrawn (ADR 0018 D13). */
export const isCurrent = (assertion: Assertion) => !assertion.withdrawn && !assertion.supersededBy;

/**
 * ADR 0018 (#915, #920; #929): a Molecule's property catalog, every value reported and each
 * property's summary (D9) -- loaded once for the whole Monograph, so the Cards view's property cards
 * and the full Properties list share it. Loads again when PubChem's facts land or refresh, since
 * their values have been imported by then (D14).
 */
export function useMoleculeProperties(moleculeId: string, factsFetchedAt: string | null): MoleculePropertiesState {
  const [loaded, setLoaded] = useState<MoleculeProperties | null>(null);
  const [failed, setFailed] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [entries, all, summaries] = await Promise.all([
        getProperties(),
        getMoleculeAssertions(moleculeId),
        getPropertySummaries(moleculeId)
      ]);
      const catalog = entries.filter((entry): entry is PropertyDefinition => !isStatementProperty(entry));
      const statements = all.filter(isStatementAssertion);
      const assertions = all.filter((assertion): assertion is Assertion => !isStatementAssertion(assertion));
      setLoaded({ catalog, assertions, summaries, statements });
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [moleculeId]);

  useEffect(() => {
    void reload();
  }, [reload, factsFetchedAt]);

  return { loaded, failed, reload };
}

/** ADR 0017 D2: definitions that read as an introduction lead About (the server's FactsheetSources order). */
const ABOUT_ORDER = ['ChEBI', 'Medical Subject Headings (MeSH)', 'NCI Thesaurus (NCIt)', 'LiverTox', 'CAMEO Chemicals'];

/**
 * #947 (S5, S8): a statement property's current statements, ready to show -- sourced imports first
 * (About in the introduction order), then people's, newest first. Null when there are none yet (the
 * caller passes PubChem's cached text, shown until the import runs).
 */
export function statementsFor(
  loaded: MoleculeProperties | null,
  propertyId: 'description' | 'use' | 'toxicity-summary',
  /** PubChem's cached text: shown, with people's statements after it, until the import has run. */
  cached?: readonly PublishedStatement[] | null
): readonly PublishedStatement[] {
  const current = (loaded?.statements ?? []).filter(
    (statement) => statement.propertyId === propertyId && !statement.withdrawn && !statement.supersededBy
  );
  if (current.length === 0) return cached ?? [];
  const rank = (statement: StatementAssertion) => {
    if (!statement.imported) return ABOUT_ORDER.length + 1;
    const index = propertyId === 'description' ? ABOUT_ORDER.indexOf(statement.imported.source) : -1;
    return index >= 0 ? index : ABOUT_ORDER.length;
  };
  // Not imported yet: the cached text first, people's statements after it.
  const imported = current.some((statement) => statement.imported);
  const ordered = [...current]
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (a.imported || b.imported ? a.createdAt.localeCompare(b.createdAt) : b.createdAt.localeCompare(a.createdAt))
    )
    .map((statement) => ({
      section: statement.imported?.reportedAs ?? '',
      text: statement.text.text,
      source: statement.imported?.source ?? statement.authorName ?? '',
      sourceUrl: statement.imported?.sourceUrl ?? statement.references[0]?.url ?? null,
      assertionId: statement.id,
      mine: statement.mine,
      standing: statement.standing,
      audience: statement.audience,
      language: statement.text.language
    }));
  return imported ? ordered : [...(cached ?? []), ...ordered];
}
